import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { differenceInCalendarDays } from 'date-fns';
import { firstValueFrom, map, skipWhile, take } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthState, selectActiveAccount } from '../../core/auth/auth.reducer';
import { TOAST_CONFIG } from '../../core/toasts/toast-config.model';
import { PaginationResponse } from '../../shared/models/pagination-response.model';
import { SelectItem } from '../../shared/models/select-item.model';
import { ServiceRequest } from './service-request.model';

@Injectable({
  providedIn: 'root',
})
export class ServiceRequestService {
  route: string = environment.apiUrl + '/api/Tickets';

  constructor(
    private _http: HttpClient,
    private _store: Store<AuthState>
  ) {}

  /**
   * Get all servicerequest items
   *
   * @returns Promise<ServiceRequest[]> with the response data
   */
  get(
    pageNumber: number,
    searchParams?: HttpParams,
    pageSize: number = 50
  ): Promise<PaginationResponse<ServiceRequest[]>> {
    searchParams = searchParams?.set('PageNumber', pageNumber.toString());
    searchParams = searchParams?.set('PageSize', pageSize.toString());

    return firstValueFrom(
      this._http.get<PaginationResponse<ServiceRequest[]>>(this.route, {
        params: searchParams,
      })
    );
  }

  /**
   * Get a servicerequest item by id
   *
   * @param id number
   * @returns Promise<ServiceRequest> with the response data
   */
  show(id: number): Promise<ServiceRequest> {
    return firstValueFrom(this._http.get<ServiceRequest>(this.route + '/' + id));
  }

  /**
   * Create a servicerequest item
   *
   * @param serviceRequest ServiceRequest
   * @returns Promise<ServiceRequest> with the response data
   */
  async create(serviceRequest: ServiceRequest): Promise<ServiceRequest> {
    const userEmail: string = await firstValueFrom(
      this._store.select(selectActiveAccount).pipe(
        skipWhile(account => account === null || account === undefined),
        take(1),
        map(account => account!.email)
      )
    );
    const formData: FormData = new FormData();

    formData.append('Title', serviceRequest.title);
    formData.append('Description', serviceRequest.description);
    formData.append('Requester', userEmail);
    formData.append('Critical', serviceRequest.critical ? 'true' : 'false');
    formData.append('Compliance', serviceRequest.compliance ? 'true' : 'false');
    formData.append('Confidential', serviceRequest.confidential ? 'true' : 'false');
    formData.append('DueDate', serviceRequest.dueDate ?? '');
    formData.append('Priority', serviceRequest?.priority?.toString() ?? '');
    formData.append('Severity', serviceRequest?.severity?.toString() ?? '');
    formData.append('Urgency', serviceRequest?.urgency?.toString() ?? '');

    // TODO: for some reason the files can be an empty string?!
    if (serviceRequest?.files) {
      serviceRequest.files?.forEach((file: File) => {
        formData.append('Files', file);
      });
    }

    return firstValueFrom(
      this._http.post<ServiceRequest>(this.route, formData, {
        context: new HttpContext().set(TOAST_CONFIG, {
          successMessage: { text: 'Service request created.' },
        }),
      })
    );
  }

  /**
   * Update a servicerequest item
   *
   * @param servicerequest ServiceRequest
   * @returns Promise<ServiceRequest> with the response data
   */
  update(servicerequest: ServiceRequest): Promise<ServiceRequest> {
    return firstValueFrom(
      this._http.put<ServiceRequest>(this.route + '/' + servicerequest.id, servicerequest)
    );
  }

  /**
   * Will start or join a teams chat regarding the servicerequest
   *
   * @param servicerequest ServiceRequest
   */
  async joinTeamsChat(servicerequest: ServiceRequest): Promise<object> {
    const userEmail: string = await firstValueFrom(
      this._store.select(selectActiveAccount).pipe(
        skipWhile(account => account === null || account === undefined),
        take(1),
        map(account => account!.email)
      )
    );
    if (servicerequest.chatId === null) {
      // Start a new chat
      return firstValueFrom(
        this._http.post(this.route + '/' + servicerequest.id + '/chat', {
          ticketId: servicerequest.id,
          requester: servicerequest.requester,
          assigned: userEmail,
        })
      );
    } else {
      // Join existing chat
      return firstValueFrom(
        this._http.put(
          this.route + '/' + servicerequest.id + '/chat/' + servicerequest.chatId + '/join',
          {
            ticketId: servicerequest.id,
            requester: userEmail,
            chatId: servicerequest.chatId,
          }
        )
      );
    }
  }

  /**
   * Contest the completing of a servicerequest
   *
   * @param ticketId
   * @returns
   */
  contestServiceRequest(ticketId: number): Promise<object> {
    return firstValueFrom(this._http.put(this.route + '/' + ticketId + '/reopen', {}));
  }

  /**
   * Will calculate the priority of the servicerequest
   *
   * @param serviceRequest ServiceRequest
   * @returns number with the priority
   */
  calculatePriority(serviceRequest: ServiceRequest): number {
    // If the service request is critical, set priority to 1
    if (serviceRequest.critical) {
      return 1;
    }

    let priority: number = 0;
    let divider: number = 0;

    // ServiceRequest due date priority
    if (serviceRequest.dueDate != '' && serviceRequest.dueDate != null) {
      divider += 1;

      priority += this.getDueDatePriority(serviceRequest);
    }

    // Handle severity
    if (serviceRequest.severity != null) {
      divider += 1;
      priority += Number((serviceRequest.severity as SelectItem).value);
    }

    // Handle urgency
    if (serviceRequest.urgency != null) {
      divider += 1;
      priority += Number((serviceRequest.urgency as SelectItem).value);
    }

    // Handle compliance
    if (serviceRequest.compliance) {
      divider += 1;
      priority += 1;
    }

    // Check if priority is NaN, if so return 4 (lowest priority)
    return Math.round(isNaN(priority / divider) ? 4 : priority / divider);
  }

  /**
   * Will return a priority based on the due date of the service request
   *
   * @param serviceRequest ServiceRequest
   * @returns number with the due date priority
   */
  getDueDatePriority(serviceRequest: ServiceRequest): number {
    const daysUntil: number = differenceInCalendarDays(
      new Date(serviceRequest.dueDate ?? ''),
      new Date()
    );

    // within 7 days (or past due)
    if (daysUntil <= 7) {
      return 1;
    }
    // within 14 days
    if (daysUntil <= 14) {
      return 2;
    }
    // within 60 days
    if (daysUntil <= 60) {
      return 3;
    }

    return 4;
  }

  /**
   * Download a file attached to a servicerequest
   *
   * @param ticketId number
   * @param guid string
   * @returns Promise<Blob | void>
   */
  downloadFile(ticketId: number, guid: string): Promise<Blob | void> {
    return firstValueFrom(
      this._http.get(this.route + '/' + ticketId + '/files/' + guid + '/download', {
        responseType: 'blob',
      })
    );
  }
}
